import React, { useState, useRef, useEffect } from 'react';
import { getCategoriesByFeatureType, searchCategories, CategoryOption } from '../../utils/imdfCategories';

interface CategorySelectProps {
  featureType: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

/**
 * 可搜尋的 Category 下拉選單元件
 * 根據 feature type 動態載入對應的 category 選項
 */
const CategorySelect: React.FC<CategorySelectProps> = ({
  featureType,
  value,
  onChange,
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState<CategoryOption[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // 載入對應的 category 選項
  useEffect(() => {
    const options = getCategoriesByFeatureType(featureType);
    setFilteredOptions(options);
  }, [featureType]);

  // 搜尋過濾
  useEffect(() => {
    const results = searchCategories(featureType, searchTerm);
    setFilteredOptions(results);
  }, [searchTerm, featureType]);

  // 點擊外部關閉下拉選單
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleSelect = (option: CategoryOption) => {
    onChange(option.value);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleInputFocus = () => {
    if (!disabled) {
      setIsOpen(true);
    }
  };

  const handleClear = () => {
    onChange('');
    setSearchTerm('');
    inputRef.current?.focus();
  };

  // 取得當前選中的 category 資訊
  const selectedOption = filteredOptions.find(opt => opt.value === value);

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={isOpen ? searchTerm : (value || '')}
          onChange={(e) => setSearchTerm(e.target.value)}
          onFocus={handleInputFocus}
          disabled={disabled}
          placeholder="搜尋或選擇 category..."
          className={`w-full p-2 pr-20 border rounded ${
            disabled
              ? 'border-gray-200 bg-gray-50 cursor-not-allowed'
              : 'border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500'
          }`}
        />
        <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex gap-1">
          {value && !disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="p-1 text-gray-400 hover:text-gray-600"
              title="清除"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          )}
          <button
            type="button"
            onClick={() => !disabled && setIsOpen(!isOpen)}
            disabled={disabled}
            className={`p-1 ${disabled ? 'text-gray-300' : 'text-gray-600 hover:text-gray-800'}`}
            title={isOpen ? '收合' : '展開'}
          >
            <svg className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
      </div>

      {/* 顯示選中項目的定義 */}
      {!isOpen && selectedOption?.definition && (
        <p className="mt-1 text-xs text-gray-500 italic">
          {selectedOption.definition}
        </p>
      )}

      {/* 下拉選單 */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
          {filteredOptions.length === 0 ? (
            <div className="p-4 text-center text-gray-500">
              沒有找到符合的類別
            </div>
          ) : (
            <ul className="py-1">
              {filteredOptions.map((option) => (
                <li
                  key={option.value}
                  onClick={() => handleSelect(option)}
                  className={`px-3 py-2 cursor-pointer hover:bg-blue-50 ${
                    value === option.value ? 'bg-blue-100' : ''
                  }`}
                >
                  <div className="font-medium text-sm">{option.value}</div>
                  {option.definition && (
                    <div className="text-xs text-gray-500 mt-0.5 line-clamp-2">
                      {option.definition}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* 統計資訊 */}
      {isOpen && (
        <div className="mt-1 text-xs text-gray-400">
          找到 {filteredOptions.length} 個選項
        </div>
      )}
    </div>
  );
};

export default CategorySelect;
